import React from 'react';
import { G1_COLOR, G2_COLOR } from './ComparisonControls';

/**
 * The headline of a comparison: each group's total, and the gap between them.
 */

/** How a number is written on these cards. */
const format = (value) =>
  Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 });

/**
 * One group's total, with a bar showing its size against the larger group.
 *
 * The bar carries the comparison on its own. It used to be captioned "Group 1
 * · 42% of the larger", which named the card after its slot rather than after
 * what it holds — the meter's name is already at the top — and restated in
 * words the length the bar had just drawn.
 *
 * @param {string} colour - The group's colour, matching its line on the chart.
 * @param {string} name - What this group is.
 * @param {number} value - Its total.
 * @param {number} peak - The larger of the two totals, which sizes the bar.
 * @param {string} unit - Unit the totals are in.
 */
const TotalCard = ({ colour, name, value, peak, unit }) => {
  const share = peak > 0 ? (Number(value) / peak) * 100 : 0;

  return (
    <div className="wc-stat" style={{ '--wc-accent': colour }}>
      <div className="wc-stat-label">
        <span className="wc-stat-swatch" style={{ background: colour }} />
        {name}
      </div>
      <div className="wc-stat-value">
        {format(value)}
        <span>{unit}</span>
      </div>
      <div className="wc-stat-track">
        <span style={{ width: `${Math.min(Math.max(share, 0), 100)}%`, background: colour }} />
      </div>
    </div>
  );
};

/**
 * The gap between the two groups.
 *
 * Water going up is not automatically bad and going down is not automatically
 * good — a treated-water figure falling is a plant treating less — so the card
 * names which group is higher rather than colouring the direction green or
 * red and telling the reader what to think about it.
 *
 * @param {object} change - Result of `changeBetween`.
 * @param {string} g1 - Group 1's name.
 * @param {string} g2 - Group 2's name.
 * @param {string} unit - Unit the totals are in.
 */
const DifferenceCard = ({ change, g1, g2, unit }) => {
  const level = change.diff === 0;
  const higher = change.diff > 0 ? g2 : g1;
  const lower = change.diff > 0 ? g1 : g2;

  return (
    <div className="wc-stat wc-stat--diff">
      <div className="wc-stat-label">Difference</div>
      <div className="wc-stat-value">
        {change.comparable
          ? `${level ? '' : change.diff > 0 ? '▲ ' : '▼ '}${Math.abs(change.percent).toFixed(1)}%`
          : '—'}
      </div>
      <div className="wc-stat-foot">
        {level ? (
          `${g1} and ${g2} are equal`
        ) : change.comparable ? (
          <>
            {format(Math.abs(change.diff))} {unit}
            <br />
            <strong>{higher}</strong> higher than {lower}
          </>
        ) : (
          `${g1} reported nothing, so there is no change to state`
        )}
      </div>
    </div>
  );
};

/**
 * @param {{name: string, total: number}} g1 - Group 1.
 * @param {{name: string, total: number}} g2 - Group 2.
 * @param {object} change - Result of `changeBetween`.
 * @param {string} unit - Unit the totals are in.
 * @param {boolean} mixedUnits - Whether the two groups report different units.
 * @param {Array<string>} units - Each group's own unit, when they differ.
 */
const ComparisonSummary = ({
  g1,
  g2,
  change,
  unit = 'm³',
  mixedUnits = false,
  units = [],
}) => {
  const peak = Math.max(g1.total, g2.total);

  return (
    <>
      <div className="wc-stats">
        <TotalCard
          colour={G1_COLOR}
          name={g1.name}
          value={g1.total}
          peak={peak}
          unit={mixedUnits ? units[0] : unit}
        />
        <TotalCard
          colour={G2_COLOR}
          name={g2.name}
          value={g2.total}
          peak={peak}
          unit={mixedUnits ? units[1] : unit}
        />
        <DifferenceCard change={change} g1={g1.name} g2={g2.name} unit={unit} />
      </div>

      {/* Two KPIs need not share a unit — a flow reports m³/h against a
          volume's m³ — and a difference across those is arithmetic on unlike
          things rather than a finding. */}
      {mixedUnits && (
        <p className="wc-warn" role="note">
          These two KPIs report in different units ({units[0]} and {units[1]}).
          The difference below is not a like-for-like comparison.
        </p>
      )}
    </>
  );
};

export default ComparisonSummary;
